/**
 * The battle mutations, all through the `/v1` proxy with the shopper's session. Each applies the
 * returned battle to the cache at once (the acting client should not wait for its own event)
 * and invalidates what else the change touched.
 */
import type {
  BattleClaimResponse,
  BattleResponse,
  BattleSellBackResponse,
  CreateBattleRequest,
} from "@loot-os/api-contract";
import { useMutation } from "@tanstack/react-query";

import { analyticsErrorCode, track } from "./analytics.js";
import {
  invalidateAfterBattleChange,
  invalidateAfterBattleClaim,
  invalidateAfterBattleEntry,
  lootFetch,
  qk,
} from "./queries.js";
import { queryClient } from "./queryClient.js";
import { captureServerTime } from "./serverClock.js";

function applyBattle(res: BattleResponse): void {
  captureServerTime(res.serverTime);
  queryClient.setQueryData(qk.battle(res.battle.id), res);
  invalidateAfterBattleChange();
}

/** Create and join: the battle applied, and the balance refetched because the entry was debited. */
function applyBattleEntry(res: BattleResponse): void {
  applyBattle(res);
  invalidateAfterBattleEntry();
}

/**
 * A refused battle action as the catalog records it: a create or join short of balance is the
 * same funnel step as an open short of balance; anything else keeps the error code.
 */
function trackBattleFailure(action: "create" | "join" | "claim" | "sell_back", err: unknown) {
  const code = analyticsErrorCode(err);
  if (code === "insufficient_balance" && (action === "create" || action === "join")) {
    track("insufficient_balance", {
      action: action === "create" ? "battle_create" : "battle_join",
    });
    return;
  }
  track("battle_action_failed", { action, code });
}

export function useCreateBattle() {
  return useMutation({
    mutationFn: (input: CreateBattleRequest) =>
      lootFetch<BattleResponse>("/battles", {
        method: "POST",
        // One key per attempt: a retried request replays the battle the first one created.
        headers: { "idempotency-key": crypto.randomUUID() },
        body: JSON.stringify(input),
      }),
    onSuccess: applyBattleEntry,
    onError: (err) => trackBattleFailure("create", err),
  });
}

export function useJoinBattle(battleId: string) {
  return useMutation({
    mutationFn: (input: { seatIndex: number; clientSeed: string }) =>
      lootFetch<BattleResponse>(`/battles/${battleId}/join`, {
        method: "POST",
        body: JSON.stringify(input),
      }),
    onSuccess: applyBattleEntry,
    onError: (err) => trackBattleFailure("join", err),
  });
}

export function useAddBot(battleId: string) {
  return useMutation({
    mutationFn: (input: { seatIndex: number }) =>
      lootFetch<BattleResponse>(`/battles/${battleId}/bots`, {
        method: "POST",
        body: JSON.stringify(input),
      }),
    onSuccess: applyBattle,
  });
}

export function useStartBattle(battleId: string) {
  return useMutation({
    mutationFn: () => lootFetch<BattleResponse>(`/battles/${battleId}/start`, { method: "POST" }),
    onSuccess: applyBattle,
  });
}

/** Mints the seat's items (or the loser prize). No body: the entry was paid at create or join. */
export function useClaimBattle(battleId: string) {
  return useMutation({
    mutationFn: () =>
      lootFetch<BattleClaimResponse>(`/battles/${battleId}/claim`, { method: "POST" }),
    onSuccess: () => invalidateAfterBattleClaim(battleId),
    onError: (err) => trackBattleFailure("claim", err),
  });
}

/** Claims the seat and sells everything it minted straight back: the pot, or the loser prize. */
export function useSellBackBattle(battleId: string) {
  return useMutation({
    mutationFn: () =>
      lootFetch<BattleSellBackResponse>(`/battles/${battleId}/sell-back`, { method: "POST" }),
    onSuccess: () => invalidateAfterBattleClaim(battleId),
    onError: (err) => trackBattleFailure("sell_back", err),
  });
}
